import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { TextareaAutosize } from "~/components/textarea-autosize/textarea-autosize";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { CrossAltIcon } from "~/components/ui/icons/cross-alt";
import { DocumentIcon } from "~/components/ui/icons/document";
import { PercentIcon } from "~/components/ui/icons/percent";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "~/components/ui/input-group";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { SidebarTitle } from "~/components/ui/sidebar";
import { toast } from "~/components/ui/toast";
import {
  SupplierBillingAdjustmentChargeTo,
  useBillingQuery,
  useCreateSupplierBillingAdjustmentMutation,
  useSupplierBillingAdjustmentQuery,
  useSupplierBillingAdjustmentsQuery,
  useUpdateSupplierBillingAdjustmentMutation,
} from "~/graphql/graphql-new.gen";
import { StoreCurrency } from "~/graphql/graphql.gen";
import { useFinancialAdjustmentAttachmentUploadMutation } from "~/graphql/hooks";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { computeHT, computeTTC } from "~/utils/price";

import { FinancialAdjustmentFileUpload } from "./financial-adjustment-file-upload";

type FinancialAdjustmentForm = {
  chargedTo: SupplierBillingAdjustmentChargeTo;
  title: string;
  description: string;
  amountVatExcluded: number;
  amountVatIncluded: number;
  vatRate: number;
  file?: File;
  attachments: {
    url: string;
  }[];
};

type FinancialAdjustmentFormProps = {
  adjustmentId?: string;
  setOpen: (isOpen: boolean) => void;
};

export const FinancialAdjustmentForm: React.FC<
  FinancialAdjustmentFormProps
> = ({ adjustmentId, setOpen }) => {
  const { user } = useAuth0();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const brand = useAtomValue(brandState);
  const activeSale = useAtomValue(activeSaleState);

  const isInternalUser = !!user?.["https://appchoose.io/claims/isInternalUser"];

  const { mutateAsync: createMutateAsync, isPending: isCreatePending } =
    useCreateSupplierBillingAdjustmentMutation();
  const { mutateAsync: updateMutateAsync, isPending: isUpdatePending } =
    useUpdateSupplierBillingAdjustmentMutation();
  const [financialAdjustmentUploadMutation, { loading, progress, error }] =
    useFinancialAdjustmentAttachmentUploadMutation();

  const { refetch } = useSupplierBillingAdjustmentQuery(
    {
      supplierBillingAdjustmentId: adjustmentId ?? "",
      withNotes: isInternalUser,
    },
    {
      enabled: false,
      select: (data) => data.supplierBillingAdjustment,
    },
  );

  const form = useForm<FinancialAdjustmentForm>({
    mode: "onTouched",
    defaultValues: async () => {
      if (adjustmentId) {
        const { data } = await refetch();

        return {
          chargedTo:
            data?.chargedTo ?? SupplierBillingAdjustmentChargeTo.Choose,
          title:
            (data?.__typename === "SupplierBillingAdjustmentDraft"
              ? data.draftTitle
              : data?.title) ?? "",
          description:
            (data?.__typename === "SupplierBillingAdjustmentDraft"
              ? data.draftDescription
              : data?.description) ?? "",
          amountVatExcluded:
            data?.__typename === "SupplierBillingAdjustmentDraft"
              ? data.draftAmountVatExcludedInCents
                ? data.draftAmountVatExcludedInCents / 100
                : (undefined as unknown as number)
              : data?.amountVatExcludedInCents
                ? data.amountVatExcludedInCents / 100
                : (undefined as unknown as number),
          amountVatIncluded:
            data?.__typename === "SupplierBillingAdjustmentDraft"
              ? data.draftAmountVatIncludedInCents
                ? data.draftAmountVatIncludedInCents / 100
                : (undefined as unknown as number)
              : data?.amountVatIncludedInCents
                ? data.amountVatIncludedInCents / 100
                : (undefined as unknown as number),
          vatRate:
            data?.__typename === "SupplierBillingAdjustmentDraft"
              ? (data.draftVatRate as unknown as number)
              : (data?.vatRate as unknown as number),
          attachments:
            data?.documents.map((document) => ({
              url: document,
            })) ?? [],
        };
      }
      return {
        chargedTo: SupplierBillingAdjustmentChargeTo.Choose,
        attachments: [],
      } as unknown as FinancialAdjustmentForm;
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "attachments",
  });

  useEffect(() => {
    if (error) {
      toast.error(
        t("invoice.financial_adjustment_form.fields.file.file_upload_error"),
      );
    }
  }, [error]);

  const chargedToOptions = [
    {
      name: "Choose",
      value: SupplierBillingAdjustmentChargeTo.Choose,
    },
    {
      name: brand?.name,
      value: SupplierBillingAdjustmentChargeTo.Supplier,
    },
  ];

  const onSubmit = async (data: FinancialAdjustmentForm) => {
    try {
      if (adjustmentId) {
        await updateMutateAsync({
          input: {
            supplierBillingAdjustmentId: adjustmentId,
            chargedTo: data.chargedTo,
            title: data.title,
            description: data.description,
            amountVatExcludedInCents: Math.round(data.amountVatExcluded * 100),
            vatRate: data.vatRate,
            documents: data.attachments.map((document) => document.url),
          },
        });
      } else {
        await createMutateAsync({
          input: {
            saleId: activeSale?.id ?? "",
            chargedTo: data.chargedTo,
            title: data.title,
            description: data.description,
            amountVatExcludedInCents: Math.round(data.amountVatExcluded * 100),
            vatRate: data.vatRate,
            documents: data.attachments.map((document) => document.url),
          },
        });
      }

      queryClient.invalidateQueries({
        queryKey: useSupplierBillingAdjustmentsQuery.getKey({
          saleId: activeSale?.id ?? "",
        }),
      });

      if (adjustmentId) {
        queryClient.invalidateQueries({
          queryKey: useBillingQuery.getKey({ saleId: activeSale?.id ?? "" }),
        });
      }

      setOpen(false);

      toast.success(t("invoice.financial_adjustment_form.success"));
    } catch {
      toast.error(t("invoice.financial_adjustment_form.error"));
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-hidden">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 pb-0 lg:p-10 lg:pb-0">
            <div className="flex flex-col gap-10 pb-10">
              <SidebarTitle>
                {t(
                  "invoice.financial_adjustment_form.add_financial_adjustment",
                )}
              </SidebarTitle>
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="chargedTo"
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "invoice.financial_adjustment_form.fields.charged_to.label",
                        )}
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={value}
                          onValueChange={onChange}
                          className="gap-3"
                        >
                          {chargedToOptions.map((option) => (
                            <div
                              className="group flex items-center space-x-3"
                              key={option.value}
                            >
                              <RadioGroupItem
                                key={option.value}
                                value={option.value}
                                id={`charged-to-${option.value}`}
                              />

                              <Label
                                htmlFor={`charged-to-${option.value}`}
                                className="cursor-pointer text-sm text-gray-700 group-has-[.peer:disabled]:cursor-default group-has-[.peer:disabled]:text-gray-300 group-has-[.peer[data-state=checked]]:text-gray-900"
                              >
                                {option.name}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title"
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "invoice.financial_adjustment_form.fields.title.label",
                        )}
                      </FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      {error && <div>{error.message}</div>}
                      <FormMessage match="required">
                        {t(
                          "invoice.financial_adjustment_form.fields.title.validation_errors.required",
                        )}
                      </FormMessage>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "invoice.financial_adjustment_form.fields.description.label",
                        )}
                      </FormLabel>
                      <FormControl>
                        <TextareaAutosize {...field} minRows={5} />
                      </FormControl>
                      <FormMessage match="required">
                        {t(
                          "invoice.financial_adjustment_form.fields.description.validation_errors.required",
                        )}
                      </FormMessage>
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="amountVatExcluded"
                    rules={{
                      min: 0,
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t(
                            "invoice.financial_adjustment_form.fields.amount_vat_excluded.label",
                          )}
                        </FormLabel>
                        <InputGroup>
                          <FormControl>
                            <InputGroupInput
                              {...field}
                              type="number"
                              data-slot="input-group-control"
                              step="0.01"
                              className="spin-button-none"
                              onChange={(event) =>
                                field.onChange(event.target.valueAsNumber)
                              }
                              onBlur={(event) => {
                                field.onBlur();
                                const vat = form.getValues("vatRate");
                                if (vat) {
                                  const amountVatExcluded =
                                    event.target.valueAsNumber;
                                  form.setValue(
                                    "amountVatIncluded",
                                    computeTTC(amountVatExcluded, vat),
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <InputGroupAddon>
                            {activeSale?.currency === StoreCurrency.Eur
                              ? "€"
                              : "$"}
                          </InputGroupAddon>
                        </InputGroup>
                        <FormMessage match="min">
                          {t(
                            "invoice.financial_adjustment_form.fields.amount_vat_excluded.validation_errors.min",
                            {
                              min: "0",
                            },
                          )}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="vatRate"
                    rules={{
                      min: 0,
                      max: 100,
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t(
                            "invoice.financial_adjustment_form.fields.vat_rate.label",
                          )}
                        </FormLabel>
                        <InputGroup>
                          <FormControl>
                            <InputGroupInput
                              {...field}
                              type="number"
                              data-slot="input-group-control"
                              step="0.01"
                              className="spin-button-none"
                              onChange={(event) =>
                                field.onChange(event.target.valueAsNumber)
                              }
                              onBlur={(event) => {
                                field.onBlur();
                                const amountVatExcluded =
                                  form.getValues("amountVatExcluded");
                                const amountVatIncluded =
                                  form.getValues("amountVatIncluded");
                                if (amountVatExcluded) {
                                  form.setValue(
                                    "amountVatIncluded",
                                    computeTTC(
                                      amountVatExcluded,
                                      event.target.valueAsNumber,
                                    ),
                                  );
                                } else if (amountVatIncluded) {
                                  form.setValue(
                                    "amountVatExcluded",
                                    computeHT(
                                      amountVatIncluded,
                                      event.target.valueAsNumber,
                                    ),
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <InputGroupAddon>
                            <PercentIcon className="size-4" />
                          </InputGroupAddon>
                        </InputGroup>
                        <FormMessage match="min">
                          {t(
                            "invoice.financial_adjustment_form.fields.vat_rate.validation_errors.min",
                            { min: "0" },
                          )}
                        </FormMessage>
                        <FormMessage match="max">
                          {t(
                            "invoice.financial_adjustment_form.fields.vat_rate.validation_errors.max",
                            {
                              max: "100",
                            },
                          )}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="amountVatIncluded"
                    rules={{
                      min: 0,
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t(
                            "invoice.financial_adjustment_form.fields.amount_vat_included.label",
                          )}
                        </FormLabel>
                        <InputGroup>
                          <FormControl>
                            <InputGroupInput
                              {...field}
                              type="number"
                              data-slot="input-group-control"
                              step="0.01"
                              className="spin-button-none"
                              onChange={(event) =>
                                field.onChange(event.target.valueAsNumber)
                              }
                              onBlur={(event) => {
                                field.onBlur();
                                const vat = form.getValues("vatRate");
                                if (vat) {
                                  const amountVatIncluded =
                                    event.target.valueAsNumber;
                                  form.setValue(
                                    "amountVatExcluded",
                                    computeHT(amountVatIncluded, vat),
                                  );
                                }
                              }}
                            />
                          </FormControl>
                          <InputGroupAddon>
                            {activeSale?.currency === StoreCurrency.Eur
                              ? "€"
                              : "$"}
                          </InputGroupAddon>
                        </InputGroup>
                        <FormMessage match="min">
                          {t(
                            "invoice.financial_adjustment_form.fields.amount_vat_included.validation_errors.min",
                            {
                              min: "0",
                            },
                          )}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          "invoice.financial_adjustment_form.fields.file.label",
                        )}
                      </FormLabel>
                      {fields.length > 0 ? (
                        <ul className="flex items-center gap-2">
                          {fields.map((field, index) => (
                            <li key={field.id} className="group relative">
                              <a
                                href={field.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center rounded border border-gray-100 p-5 text-sm text-gray-700 group-hover:border-gray-700"
                              >
                                <DocumentIcon className="size-6" />
                              </a>
                              <div className="absolute right-[-7px] top-[-7px] hidden group-hover:flex">
                                <button
                                  type="button"
                                  className="rounded-full bg-gray-900 p-1"
                                  onClick={() => remove(index)}
                                >
                                  <CrossAltIcon className="text-white" />
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <FormControl>
                        <FinancialAdjustmentFileUpload
                          value={field.value}
                          onValueChange={async (file) => {
                            field.onChange(file);

                            const result =
                              await financialAdjustmentUploadMutation({
                                saleId: activeSale?.id ?? "",
                                file,
                              });

                            if (result) {
                              const url = new URL(result.uploadedUrl);
                              url.search = "";
                              append({ url: url.toString() });
                            }

                            form.resetField("file");
                          }}
                          isUploading={loading}
                          progress={progress}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <footer className="flex shrink-0 justify-between p-4 lg:px-10">
          <Button type="submit" disabled={isCreatePending || isUpdatePending}>
            {t("invoice.financial_adjustment_form.save")}
          </Button>
        </footer>
      </form>
    </Form>
  );
};
